import React, { useRef, useEffect, useState } from 'react';
import { ClickAwayListener } from '@mui/material';

interface IgnorableClickAwayListenerProps {
  onClickAway: () => void;
  ignoreId: string;
  children: React.ReactElement;
}

// When there is button outside of this context that does the same thing
// this listener fires before it, button click then can change the state back
// so we need a way to ignore the click event when button is clicked.
// For now i chose id as a way to identify the button but we can extend it when needed
export const IgnorableClickAwayListener = ({ onClickAway, ignoreId, children }: IgnorableClickAwayListenerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  useEffect(() => {
    const checkSelectOpen = () => {
      if (!containerRef.current) return;

      // Check if any Select within the container has aria-expanded="true"
      const openSelect = containerRef.current.querySelector('[role="combobox"][aria-expanded="true"]');
      setIsSelectOpen(!!openSelect);
    };

    // Use MutationObserver to watch for aria-expanded changes
    const observer = new MutationObserver(() => {
      checkSelectOpen();
    });

    if (containerRef.current) {
      observer.observe(containerRef.current, {
        attributes: true,
        attributeFilter: ['aria-expanded'],
        subtree: true,
      });
      // Initial check
      checkSelectOpen();
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleClickAway = (event: Event) => {
    const target = event.target as Element;
    const ignoreButton = document.getElementById(ignoreId);

    if (ignoreButton && (ignoreButton.contains(target) || ignoreButton === target)) {
      return;
    }

    // Check if the click is within the container
    if (containerRef.current && containerRef.current.contains(target)) {
      return;
    }

    // If a Select is open, don't close the component
    // The Select will handle closing itself
    if (isSelectOpen) {
      return;
    }

    onClickAway();
  };

  return (
    <div ref={containerRef}>
      <ClickAwayListener onClickAway={handleClickAway} disableReactTree={isSelectOpen}>
        {children}
      </ClickAwayListener>
    </div>
  );
};
